import {
    AfterViewInit,
    Component,
    ElementRef,
    HostListener,
    inject,
    OnInit,
    Renderer2,
    signal,
    viewChild,
} from '@angular/core';
import { NavigationEnd, NavigationStart, Router, RouterOutlet } from '@angular/router';
import { environment } from '@env/environment';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import { AppConfig } from './app.config';
import { Customer } from './core/enums/customer.enum';
import { EventsService } from './core/services/event.service';
import { IdleDialogComponent } from './shared/components/idle-dialog/idle-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { MaintenanceService } from './shared/services/maintenance.service';
import { AppStore } from '@app/stores/appStore';
import { HeaderComponent } from './core/components/header/header.component';
import { GlobalFooterComponent } from './core/components/global-footer/global-footer.component';
import { SelfserviceFooterComponent } from './core/components/selfservice-footer/selfservice-footer.component';
import { NgxSpinnerComponent } from 'ngx-spinner';

@UntilDestroy({ checkProperties: true })
@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    imports: [
        HeaderComponent,
        RouterOutlet,
        GlobalFooterComponent,
        SelfserviceFooterComponent,
        NgxSpinnerComponent,
    ],
})
export class AppComponent implements OnInit, AfterViewInit {
    private appStore = inject(AppStore);
    showHeader = signal(true);
    isSelfService = signal(false);
    contentArea = viewChild<ElementRef>('contentArea');
    get maintenanceMode(): boolean { return this.appStore.maintenanceMode(); }
    private retryCount = 0;
    private maxRetries = 100;
    private retryDelay = 300;

    constructor(
        private renderer: Renderer2,
        private translate: TranslateService,
        private router: Router,
        private appConfig: AppConfig,
        private eventsService: EventsService,
        private dialog: MatDialog,
        // Injected to ensure service is instantiated (starts maintenance polling)
        private _maintenanceService: MaintenanceService
    ) {}

    async ngOnInit() {
        this.eventsService
            .subscribe('user-idle:show')
            .pipe(untilDestroyed(this))
            .subscribe(() => {
                const dialogRef = this.dialog.open(IdleDialogComponent, {
                    width: '500px',
                    disableClose: true,
                });

                dialogRef.afterClosed().subscribe(result => {
                    if (result) {
                        location.reload();
                    }
                });
            });

        this.router.events.pipe(untilDestroyed(this)).subscribe(event => {
            if (event instanceof NavigationEnd) {
                const showNoHeader = [
                    '/forgot-password',
                    '/check-email',
                    '/set-new-password',
                    '/maintenance',
                    '/external/reset-user-password',
                    '/selfservice',
                ];

                this.showHeader.set(!showNoHeader.includes(
                    event.urlAfterRedirects
                ));
                this.isSelfService.set(
                    event.urlAfterRedirects.startsWith('/selfservice'));
                if (!this.isSelfService()) {
                    sessionStorage.removeItem('reloadRoute');
                }
                const reloadRoute = sessionStorage.getItem('reloadRoute');
                const currentUrl = this.router.url;
                if (reloadRoute) {
                    sessionStorage.removeItem('reloadRoute');
                    if (
                        reloadRoute.startsWith('/selfservice') &&
                        currentUrl.startsWith('/selfservice')
                    ) {
                        this.router.navigateByUrl('/selfservice');
                        this.retryAdjustContentHeight();
                    }
                }
            }
        });

        this.translate.setDefaultLang(environment.defaultLang);
        this.translate.use(environment.defaultLang);
    }

    ngAfterViewInit() {
        if (this.isSelfService()) {
            this.adjustContentHeight();
        }
    }

    @HostListener('window:resize')
    onResize() {
        if (this.isSelfService()) {
            this.adjustContentHeight();
        }
    }

    @HostListener('window:beforeunload')
    onBeforeUnload() {
        const currentUrl = this.router.url;
        if (currentUrl.startsWith('/selfservice')) {
            sessionStorage.setItem('reloadRoute', currentUrl);
        } else {
            sessionStorage.removeItem('reloadRoute');
        }
    }

    private adjustContentHeight() {
        const windowHeight = window.innerHeight;
        const adjustedHeight = windowHeight;
        this.renderer.setStyle(
            this.contentArea()!.nativeElement,
            'height',
            `${adjustedHeight}px`
        );
    }

    private retryAdjustContentHeight() {
        if (this.isSelfService()) {
            if (this.retryCount < this.maxRetries) {
                setTimeout(() => {
                    if (this.contentArea() && this.contentArea()!.nativeElement) {
                        this.adjustContentHeight();
                    } else {
                        this.retryCount++;
                        this.retryAdjustContentHeight();
                    }
                }, this.retryDelay);
            }
        }
    }
}
